import { useEffect } from 'react';
import { SETUP_STEPS, useSetupFlow } from '@/pages/mt5-setup/SetupFlowContext';

const stageStatus = ['READY', 'INSTALLING', 'CONFIGURING', 'RUNNING'];

// A guided, self-advancing walkthrough of the three setup stages. It advances
// once from the first stage through to the last, then stops on "Run" so the
// right side of the hero reads as a completed flow rather than a static list.
function SetupFlow() {
  const { active, setActive, stepCount, goToStep } = useSetupFlow();

  useEffect(() => {
    if (active >= stepCount) return undefined;
    const timer = window.setTimeout(() => {
      setActive((v) => (v >= stepCount ? v : v + 1));
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [active, setActive, stepCount]);

  const done = Math.min(active, stepCount);
  const progressPct = (done / stepCount) * 100;

  return (
    <div className="w-full rounded-lg border border-background-300 bg-background-50 overflow-hidden">
      <div className="flex items-center justify-between px-4 md:px-5 h-12 border-b border-background-200 bg-background-100">
        <span className="text-[12px] font-data tracking-widest text-foreground-700">
          SETUP SEQUENCE
        </span>
        <span className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500 breathe"></span>
          <span className="text-[10px] font-data tracking-widest text-primary-600">
            {stageStatus[active]}
          </span>
        </span>
      </div>

      <div className="px-4 md:px-5 py-5">
        <div className="flex flex-col">
          {SETUP_STEPS.map((step, idx) => {
            const lit = idx < active;
            const isLast = idx === SETUP_STEPS.length - 1;
            return (
              <div key={step.index}>
                <button
                  type="button"
                  onClick={() => goToStep(idx + 1)}
                  aria-label={`Jump to Step ${step.index}: ${step.title}`}
                  className={`group w-full text-left cursor-pointer flex items-center gap-3.5 rounded-lg border px-3.5 py-3 transition-all duration-500 ${
                    lit
                      ? 'border-primary-300 bg-primary-50'
                      : 'border-background-200 bg-background-50 hover:border-primary-200 hover:bg-primary-50/40'
                  }`}
                >
                  <span
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-all duration-500 ${
                      lit ? 'bg-primary-500' : 'bg-background-100 border border-background-200'
                    }`}
                  >
                    <i
                      className={`${step.icon} text-[17px] transition-colors duration-500 ${
                        lit ? 'text-background-50' : 'text-foreground-400'
                      }`}
                    ></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <span
                      className={`font-data text-[10px] tracking-[0.2em] transition-colors duration-500 ${
                        lit ? 'text-primary-600' : 'text-foreground-400'
                      }`}
                    >
                      {step.index}
                    </span>
                    <p
                      className={`font-heading font-bold tracking-tight text-[16px] leading-tight transition-colors duration-500 ${
                        lit ? 'text-foreground-950' : 'text-foreground-500'
                      }`}
                    >
                      {step.title}
                    </p>
                  </div>
                  <span
                    className={`text-[10px] font-data tracking-widest transition-colors duration-500 ${
                      lit ? 'text-primary-600' : 'text-foreground-300'
                    }`}
                  >
                    {step.tag}
                  </span>
                  <i
                    className={`ri-arrow-right-line text-[16px] shrink-0 transition-all duration-500 ${
                      lit
                        ? 'text-primary-500 translate-x-0 opacity-100'
                        : 'text-foreground-300 -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'
                    }`}
                  ></i>
                </button>
                {!isLast && (
                  <div className="flex justify-center">
                    <span
                      className={`w-px h-5 transition-colors duration-500 ${
                        idx < active - 1 ? 'bg-primary-400' : 'bg-background-300'
                      }`}
                    ></span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-4 md:px-5 py-4 border-t border-background-200 bg-background-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-data tracking-widest text-foreground-500">
            SETUP PROGRESS
          </span>
          <span className="text-[10px] font-data tracking-widest text-primary-600">
            {done} / {stepCount}
          </span>
        </div>
        <div className="h-1 rounded-full bg-background-300 overflow-hidden">
          <div
            className="h-full rounded-full bg-primary-500 transition-all duration-500 ease-out"
            style={{ width: `${progressPct}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
}

export default SetupFlow;